import React, { useEffect, useState } from 'react';
import { Search, Download } from 'lucide-react';
import { Card, Button, Input, Badge } from '../components/UI';
import Pagination from '../components/Pagination';
import { subscriptionHistoryAPI } from '../api/apiClient';
import toast from 'react-hot-toast';

const SubscriptionHistoryPage = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    loadHistory();
  }, [page, statusFilter, search, startDate, endDate]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      const response = await subscriptionHistoryAPI.getAll(
        page,
        20,
        statusFilter,
        search,
        startDate,
        endDate
      );
      setHistory(response.data.data.history);
      setTotalPages(response.data.data.pagination.pages);
    } catch (error) {
      toast.error('Failed to load subscription history');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await subscriptionHistoryAPI.exportCSV({
        status: statusFilter,
        search,
        startDate,
        endDate,
      });

      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'subscription-history.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('Subscription history exported');
    } catch (error) {
      toast.error('Failed to export history');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Subscription History</h1>
          <p className="text-gray-400 mt-2">View completed subscriptions and export records.</p>
        </div>
        <Button onClick={handleExport}>
          <Download size={18} />
          <span>Export CSV</span>
        </Button>
      </div>

      <Card>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-3 text-gray-400" />
            <input
              className="w-full pl-11 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="Search history"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <select
            className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="expired">Expired</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <Input
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(event) => setStartDate(event.target.value)}
          />
          <Input
            label="End Date"
            type="date"
            value={endDate}
            onChange={(event) => setEndDate(event.target.value)}
          />
        </div>

        {loading ? (
          <div className="text-center py-16 text-gray-400">Loading history...</div>
        ) : history.length === 0 ? (
          <div className="text-center py-16 text-gray-400">No subscription history records found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-700 text-gray-400 uppercase text-xs tracking-wide">
                  <th className="px-4 py-3">Device UUID</th>
                  <th className="px-4 py-3">Package</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Payment</th>
                  <th className="px-4 py-3">Start</th>
                  <th className="px-4 py-3">Expiry</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {history.map((record) => (
                  <tr key={record._id} className="border-b border-gray-700 hover:bg-gray-800 transition">
                    <td className="px-4 py-4 text-white">{record.deviceUUID}</td>
                    <td className="px-4 py-4 text-gray-300">{record.packageName}</td>
                    <td className="px-4 py-4 text-gray-300">{record.amount} TK</td>
                    <td className="px-4 py-4 text-gray-300">{record.paymentMethod}</td>
                    <td className="px-4 py-4 text-gray-300">{new Date(record.startDate).toLocaleDateString()}</td>
                    <td className="px-4 py-4 text-gray-300">{new Date(record.expiryDate).toLocaleDateString()}</td>
                    <td className="px-4 py-4">
                      <Badge
                        variant={
                          record.status === 'active'
                            ? 'success'
                            : record.status === 'expired'
                            ? 'warning'
                            : 'danger'
                        }
                      >
                        {record.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Pagination currentPage={page} totalPages={totalPages} onPageChange={(newPage) => setPage(newPage)} />
      </Card>
    </div>
  );
};

export default SubscriptionHistoryPage;
